import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import { Pagination as PaginationType } from "@sscs/types";
import { DataTable, DataTableColumn, QueryResult } from "@sscs/ui";
import { formatCount, getQueryStatus } from "@sscs/utils";
import { useAppDispatch, useAppSelector } from "Hooks";
import useSearch from "../../../hooks/useSearch";
import { useGetIssueVersionsQuery } from "../../../services/insights.api";
import {
  expandedRowPaginationUpdated,
  expandedRowSortUpdated,
  selectExpandedRowPagination,
  selectExpandedRowSort,
} from "../../../store";
import { InsightType, InsightVersionsSort } from "../../../types";
import Version from "./Version/Version";
import styles from "./versions.module.scss";

type VersionProps = {
  issueId: string;
  issueType: InsightType;
  affectedVersions: number;
};

const COLUMNS: DataTableColumn<InsightVersionsSort["property"]>[] = [
  { id: "info", label: "Info", width: "70px" },
  { id: "status", label: "Status", width: "90px" },
  { id: "approval", label: "Approval", width: "150px" },
  { id: "purl", label: "PURL", sortProperty: "purl", width: "100%" },
  { id: "safe", label: "SAFE assessment", width: "400px" },
  {
    id: "released",
    label: "Released",
    sortProperty: "release",
    align: "center",
    width: "130px",
  },
];

const Versions = ({
  issueId,
  issueType,
  affectedVersions,
}: VersionProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { groupId } = useParams();
  const [focusedRow, setFocusedRow] = useState<string | null>(null);
  const pagination = useAppSelector((state) =>
    selectExpandedRowPagination(state, groupId, issueId),
  );
  const sort = useAppSelector((state) =>
    selectExpandedRowSort(state, groupId, issueId),
  );
  const { filters, isSearchFetching, savedSearch } = useSearch();
  const {
    data: versions = { match_count: 0, versions: [] },
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetIssueVersionsQuery(
    groupId && !isSearchFetching
      ? {
          filters,
          groupId,
          pagination,
          sort,
          id: issueId,
          type: issueType,
        }
      : skipToken,
  );

  const handlePagination = (newPagination: PaginationType) => {
    if (groupId) {
      dispatch(
        expandedRowPaginationUpdated({
          groupId,
          pagination: newPagination,
          issueId: issueId,
        }),
      );
    }
  };

  const handleSort = (newSort: InsightVersionsSort) => {
    if (groupId) {
      dispatch(
        expandedRowSortUpdated({ groupId, sort: newSort, issueId: issueId }),
      );
    }
  };

  return (
    <div>
      <QueryResult status={getQueryStatus(savedSearch)}>
        <DataTable
          className={styles.table}
          title="Versions"
          titleDetails={[
            affectedVersions !== versions.match_count
              ? `${formatCount(versions.match_count, "report")} in ${formatCount(affectedVersions, "version")}`
              : formatCount(versions.match_count, "version"),
          ]}
          chrome="card"
          size="large"
          columns={COLUMNS}
          sort={sort}
          onSortChange={handleSort}
          empty={{ subject: "issues" }}
          status={getQueryStatus({
            data: versions,
            isFetching,
            isLoading,
            isError,
            error,
          })}
          pagination={{
            id: "insights-details-pagination",
            itemCount: versions.match_count,
            pagination,
            onChange: handlePagination,
          }}
        >
          {versions.versions.map((version, index) => (
            <Version
              key={version.scan_report_id}
              issueType={issueType}
              issueId={issueId}
              version={version}
              rowIndex={index}
              isFocused={focusedRow === version.scan_report_id}
              onFocusChange={() => setFocusedRow(version.scan_report_id)}
            />
          ))}
        </DataTable>
      </QueryResult>
    </div>
  );
};

export default Versions;
